import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import type { Farmacia } from '../../data/farmacie'
import { FarmaciaDialog } from '../StrutturaDetailDialog'

const PAGE_SIZE = 12

interface Props {
  farmacie: Farmacia[]
}

export default function FarmacieDialogList({ farmacie }: Props) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [detailFarmacia, setDetailFarmacia] = useState<Farmacia | null>(null)

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    if (!term) return farmacie
    return farmacie.filter(f => {
      const text = `${f.ragioneSociale} ${f.comune} ${f.indirizzo}`.toLowerCase()
      return text.includes(term)
    })
  }, [farmacie, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  if (safePage !== page) setPage(safePage)

  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', minHeight: '50vh' }}>
      <div style={{ flexShrink: 0, position: 'relative', marginBottom: 10 }}>
        <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
        <input
          type="text"
          placeholder="Cerca farmacia…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          style={{
            width: '100%',
            padding: '7px 8px 7px 28px',
            fontSize: 12,
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--surface-2, var(--background))',
            color: 'var(--text-1)',
            outline: 'none',
          }}
        />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'grid', gap: 2 }}>
        {paged.map(f => (
          <div
            key={f.id}
            onClick={() => setDetailFarmacia(f)}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 8,
              padding: '7px 8px',
              borderRadius: 6,
              borderLeft: '3px solid #059669',
              background: 'var(--surface-2, transparent)',
              cursor: 'pointer',
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', wordBreak: 'break-word' }}>
                {f.ragioneSociale}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>
                {f.indirizzo}{f.indirizzo && f.comune ? ' · ' : ''}{f.comune ? f.comune.charAt(0).toUpperCase() + f.comune.slice(1).toLowerCase() : ''}
              </div>
            </div>
            {f.comune && (
              <span style={{
                flexShrink: 0,
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 7px',
                borderRadius: 999,
                background: '#05966918',
                color: '#059669',
                border: '1px solid #05966930',
                whiteSpace: 'nowrap',
              }}>
                {f.comune.charAt(0).toUpperCase() + f.comune.slice(1).toLowerCase()}
              </span>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center' }}>
            Nessuna farmacia trovata
          </div>
        )}
      </div>

      <FarmaciaDialog farmacia={detailFarmacia} onClose={() => setDetailFarmacia(null)} fromUrl="/farmacie" />

      {totalPages > 1 && (
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          marginTop: 8,
        }}>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            style={{
              background: 'none', border: 'none', cursor: safePage === 0 ? 'default' : 'pointer',
              color: safePage === 0 ? 'var(--text-3)' : 'var(--text-1)', padding: 4,
              opacity: safePage === 0 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            style={{
              background: 'none', border: 'none', cursor: safePage >= totalPages - 1 ? 'default' : 'pointer',
              color: safePage >= totalPages - 1 ? 'var(--text-3)' : 'var(--text-1)', padding: 4,
              opacity: safePage >= totalPages - 1 ? 0.4 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  )
}
